<template>
  <p class="body-3-bold tool-desc text-center text-base pb-4">
    {{ $t("calculator-suite.lotsizeDesc") }}
  </p>
  <div class="calculator-params-results">
    <section class="params-card">
      <h2 class="body-3-bold">{{ $t("calculator-suite.setParams") }}</h2>
      <div class="inputs">
        <InputSearch
          id="pivot-point-options"
          v-model="pivot"
          name="pivot-point-dropdown"
          :label="$t('calculator-suite.pivot')"
          :placeholder="$t('calculator-suite.pivotPlaceh')"
          :options="
            options.map((i) => ({
              text: $t(i.text),
              value: i.value,
            }))
          "
        />
        <div v-for="input in filteredParams" :key="`${pivot}-${input.key}`">
          <TVInputNumber
            v-if="pivot"
            v-model="input.value"
            :label="$t(input.text)"
            :placeholder="$t(input.placeholder)"
            :name="`${pivot}-${input.key}`"
            :tooltip-message="$t(input.tooltip)"
            tooltip-dir-desk="right"
            tooltip-dir-mobile="top"
            :tooltip-max-width="246"
          />
        </div>

        <ErrorMessage v-if="errorMessage" :msg="errorMessage" />
      </div>
      <button
        class="primary-button-medium"
        @click="calculate"
        :disabled="!isFormValid || isLoading"
      >
        {{ $t("calculator-suite.calculate") }}
      </button>
    </section>
    <section class="results-card">
      <ResultsSkeleton v-if="isLoading" class="p-3" />
      <div v-else>
        <h2 class="body-3-bold mb-4">{{ $t("calculator-suite.results") }}</h2>
        <ul class="table">
          <li class="table-headers">
            <span>{{ $t("calculator-suite.level") }}</span>
            <span>{{ $t("calculator-suite.price") }}</span>
          </li>
          <li
            v-for="(item, index) in filteredResults"
            :key="index"
            class="table-items"
          >
            <span class="body-4 flex gap-2 align-items-center">
              {{ $t(item.text) }}
              <Tooltip
                v-if="item.tooltip"
                :max-width-tooltip="246"
                float-tooltip-mobile="right"
                float-tooltip-desk="right"
                use-material-icon="info"
                @click="clearInput"
                style="cursor: pointer"
              >
                <template #title>
                  <p v-html="item.tooltip" />
                </template>
              </Tooltip>
            </span>
            <span class="sm:ml-4 ml-auto">{{ item.value }}</span>
          </li>
        </ul>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, watch, defineEmits } from "vue";
import { useI18n } from "vue-i18n";
import TVInputNumber from "../InputNumber.vue";
import InputSearch from "../InputSearch.vue";
import Tooltip from "../Tooltip.vue";
import ResultsSkeleton from "./ResultsSkeleton.vue";
import ErrorMessage from "./ErrorMessage.vue";
import { pivotCalculate } from "./utils/calculateRequest";
import { resultKeyMap } from "./utils/dropdownOptions";

const { t } = useI18n();
const pivot = ref("");
const isLoading = ref(false);
const errorMessage = ref(null);

const options = [
  { text: t("calculator-suite.camarillaPivot"), value: "camarilla" },
  { text: t("calculator-suite.demarkPivot"), value: "demark" },
  { text: t("calculator-suite.pivotPoint"), value: "pivotpoint" },
  { text: t("calculator-suite.woodiePivot"), value: "woodie" },
];

const params = ref([
  {
    text: "calculator-suite.highprice",
    placeholder: "calculator-suite.enterAmount",
    tooltip: "calculator-suite.highSwingTooltip",
    key: "highest_price",
    value: null,
  },
  {
    text: "calculator-suite.lowestprice",
    placeholder: "calculator-suite.enterAmount",
    tooltip: "calculator-suite.customValTooltip",
    key: "lowest_price",
    value: null,
  },
  {
    text: "calculator-suite.openprice",
    placeholder: "calculator-suite.enterAmount",
    tooltip: "calculator-suite.openpriceTooltip",
    key: "opening_price",
    value: null,
  },
  {
    text: "calculator-suite.closeprice",
    placeholder: "calculator-suite.enterAmount",
    tooltip: "calculator-suite.closepriceTooltip",
    key: "closing_price",
    value: null,
  },
]);

const results = ref([
  {
    text: "calculator-suite.pivotPointt",
    value: null,
    tooltip: t("calculator-suite.pivotPointTooltip"),
    key: "pivot_point",
  },
  {
    text: "calculator-suite.firstSupport",
    value: null,
    tooltip: t("calculator-suite.firstSupportTooltip"),
    key: "s1",
  },
  {
    text: "calculator-suite.firstRes",
    value: null,
    tooltip: t("calculator-suite.firstResTooltip"),
    key: "r1",
  },
  {
    text: "calculator-suite.secondSupport",
    value: null,
    key: "s2",
  },
  {
    text: "calculator-suite.secondRes",
    value: null,
    key: "r2",
  },
  {
    text: "calculator-suite.breakPointt",
    value: null,
    tooltip: t("calculator-suite.breakPointtTooltip"),
    key: "breakout_pivot",
  },
  {
    text: "calculator-suite.lowertarget",
    value: null,
    tooltip: t("calculator-suite.lowertargetTooltip"),
    key: "lower_target",
  },
  {
    text: "calculator-suite.uppertarget",
    value: null,
    tooltip: t("calculator-suite.uppertargetTooltip"),
    key: "upper_target",
  },
]);

watch(
  () => pivot.value,
  (value) => {
    params.value.forEach((param) => {
      param.value = null;
    });
    results.value.forEach((result) => {
      result.value = null;
    });
    errorMessage.value = null;
  }
);

const filteredParams = computed(() => {
  if (!pivot.value) return [];

  const excludeKeys = [];

  if (pivot.value.includes("pivotpoint") || pivot.value.includes("camarilla")) {
    excludeKeys.push("opening_price");
  }

  return params.value.filter((param) => !excludeKeys.includes(param.key));
});

const filteredResults = computed(() => {
  if (!pivot.value) return [];

  const excludeKeys = [];

  if (pivot.value.includes("demark")) {
    excludeKeys.push("s1", "s2", "r1", "r2", "pivot_point");
  } else {
    excludeKeys.push("breakout_pivot", "lower_target", "upper_target");
  }

  return results.value.filter((result) => !excludeKeys.includes(result.key));
});

const isFormValid = computed(() => {
  if (!pivot.value) return false;
  return filteredParams.value.every(
    (param) => param.value !== null && param.value !== ""
  );
});

const calculate = async () => {
  const paramsData = params.value.reduce((acc, { key, value }) => {
    if (value !== "") acc[key] = Number(value);
    return acc;
  }, {});

  try {
    isLoading.value = true;
    errorMessage.value = null;
    const apiResponse = await pivotCalculate(pivot.value, paramsData);

    Object.entries(apiResponse).forEach(([key, value]) => {
      const mappedKey = resultKeyMap[key];
      const resultItem = results.value.find((r) => r.key === mappedKey);
      if (resultItem) resultItem.value = value;
    });
  } catch (error) {
    console.error(error);
    if (error.message.includes("'high' must be greater than 'low'")) {
      errorMessage.value = t("calculator-suite.priceBetween");
    } else {
      errorMessage.value = t("calculator-suite.priceHigher");
    }
  } finally {
    isLoading.value = false;
  }
};
</script>
